import { useEffect, useRef, useCallback, useState } from 'react';
import { useWizard } from '../context/WizardContext';
import { analyticsService } from '../services/analyticsService';
import { progressStorageService } from '../services/progressStorageService';

interface UseProgressTrackingOptions {
  stepName: string;
  stepNumber: number;
}

export const useProgressTracking = ({ stepName, stepNumber }: UseProgressTrackingOptions) => {
  const { wizardData, updateWizardData } = useWizard();
  const [isSaving, setIsSaving] = useState(false);
  const stepStartTime = useRef<number>(Date.now());
  const lastSaveTime = useRef<number>(Date.now());
  const fieldChangeTimeout = useRef<NodeJS.Timeout | null>(null);
  const hasTrackedStart = useRef(false);
  const hasSavedForStep = useRef(false);

  // Initialize session ID if not present
  useEffect(() => {
    if (!wizardData.sessionId) {
      const sessionId = progressStorageService.generateSessionId();
      const now = new Date();
      const expirationTime = new Date(now);
      expirationTime.setHours(expirationTime.getHours() + 24); // 24-hour expiration
      
      updateWizardData({ 
        sessionId,
        sessionStartTime: now.toISOString(),
        expiresAt: expirationTime.toISOString()
      });
      
      // Track form started only once per session
      if (!hasTrackedStart.current && stepNumber === 0) {
        analyticsService.trackFormStarted(
          wizardData.customerId?.toString() || 'unknown',
          sessionId
        );
        hasTrackedStart.current = true;
      }
    }
  }, [wizardData.sessionId, wizardData.customerId, updateWizardData, stepNumber]);

  // Track step entry
  useEffect(() => {
    stepStartTime.current = Date.now();
    
    // Track page view
    analyticsService.trackPageView(
      `Wizard - ${stepName}`,
      wizardData.customerId?.toString()
    );
    
    return () => {
      // Track step completion when leaving the step
      const timeSpent = Date.now() - stepStartTime.current;
      
      if (wizardData.sessionId) {
        analyticsService.trackStepCompleted(
          stepName,
          stepNumber,
          timeSpent,
          wizardData.sessionId,
          wizardData.customerId?.toString() || 'unknown'
        );
      }
    };
  }, [stepName, stepNumber, wizardData.sessionId, wizardData.customerId]);

  // Save progress function
  const saveProgress = useCallback(async (isComplete: boolean = false) => {
    // Don't save if no session ID or if on Thank You step (7) with form completed (unless explicitly marking as complete)
    if (!wizardData.sessionId || (stepNumber === 7 && wizardData.completed && !isComplete)) return;
    
    setIsSaving(true);
    try {
      await progressStorageService.saveProgress(
        wizardData,
        stepNumber,
        stepName,
        wizardData.sessionId,
        isComplete
      );
      lastSaveTime.current = Date.now();
      
      // Update last saved timestamp in wizard data
      updateWizardData({
        lastSaved: new Date().toISOString()
      });
    } catch (error) {
      console.error('Failed to save progress:', error);
      analyticsService.trackException(error as Error);
    } finally {
      setIsSaving(false);
    }
  }, [wizardData, stepNumber, stepName, updateWizardData]);

  // Auto-save on step completion
  useEffect(() => {
    // Don't auto-save on the welcome step (step 0) or on Thank You step (step 7) if form is completed
    const shouldSkipSave = stepNumber === 0 || (stepNumber === 7 && wizardData.completed);
    
    if (stepNumber > 0 && !hasSavedForStep.current && !shouldSkipSave) {
      const saveTimer = setTimeout(() => {
        hasSavedForStep.current = true;
        saveProgress(false);
      }, 1000); // Save 1 second after entering a new step
      
      return () => clearTimeout(saveTimer);
    }
    
    // Reset the flag when step changes
    return () => {
      hasSavedForStep.current = false;
    };
  }, [stepNumber, wizardData.completed]); // Depend on stepNumber and completed status

  // Track field changes (debounced)
  const trackFieldChange = useCallback((fieldName: string, value: any) => {
    // Don't track if no session or if on Thank You step with form completed
    if (!wizardData.sessionId || (stepNumber === 7 && wizardData.completed)) return;
    
    // Clear existing timeout
    if (fieldChangeTimeout.current) {
      clearTimeout(fieldChangeTimeout.current);
    }
    
    // Set new timeout for tracking and saving
    fieldChangeTimeout.current = setTimeout(() => {
      // Track the field change
      analyticsService.trackFieldChange(
        fieldName,
        value !== null && value !== undefined && value !== '',
        stepName,
        wizardData.sessionId!
      );
      
      // Auto-save after field changes (debounced to 2 seconds)
      if (Date.now() - lastSaveTime.current > 2000) {
        saveProgress(false);
      }
    }, 2000);
  }, [wizardData.sessionId, wizardData.completed, stepName, stepNumber, saveProgress]);

  // Track validation errors
  const trackValidationError = useCallback((fieldName: string, errorType: string) => {
    if (!wizardData.sessionId) return;
    
    analyticsService.trackValidationError(
      fieldName,
      errorType,
      stepName,
      wizardData.sessionId
    );
  }, [wizardData.sessionId, stepName]);

  // Track navigation
  const trackNavigation = useCallback((action: 'next' | 'back', toStep: string) => {
    if (!wizardData.sessionId) return;
    
    analyticsService.trackNavigation(
      action,
      stepName,
      toStep,
      wizardData.sessionId
    );
  }, [wizardData.sessionId, stepName]);

  // Track form submission
  const trackFormSubmission = useCallback(() => {
    if (!wizardData.sessionId || !wizardData.sessionStartTime) return;
    
    const totalTime = Date.now() - new Date(wizardData.sessionStartTime).getTime();
    
    analyticsService.trackFormSubmitted(
      wizardData.sessionId,
      wizardData.customerId?.toString() || 'unknown',
      totalTime
    );
    
    // Save final progress as complete
    saveProgress(true);
  }, [wizardData.sessionId, wizardData.customerId, wizardData.sessionStartTime, saveProgress]);

  // Track form abandonment (on unmount if not complete)
  useEffect(() => {
    return () => {
      if (!wizardData.completed && wizardData.sessionId && wizardData.sessionStartTime) {
        const timeSpent = Date.now() - new Date(wizardData.sessionStartTime).getTime();
        
        analyticsService.trackFormAbandoned(
          stepName,
          stepNumber,
          timeSpent,
          wizardData.sessionId,
          wizardData.customerId?.toString() || 'unknown'
        );
      }
    };
  }, [wizardData.completed, wizardData.sessionId, wizardData.customerId, wizardData.sessionStartTime, stepName, stepNumber]);

  // No cleanup needed - data persists until manually deleted

  return {
    trackFieldChange,
    trackValidationError,
    trackNavigation,
    trackFormSubmission,
    saveProgress,
    isSaving,
    sessionId: wizardData.sessionId
  };
};